import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import Table from '@awp/components/table';
import { sortByNumericThenAlpha } from '@awp/helpers/sorter';
import { Row, Col, Button, Form } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { Views, Permissions, FORM_MODE } from '@awp/constants';
import { isValueEmpty } from '@awp/helpers/utilsReact';
import Authorize from '@awp/components/Authorize';
import { ActionMenuView, ActionMenuEdit } from '@awp/components/Common/AwpActionMenus';
import { IconCaretDownFill, IconCaretRightFill } from '@awp/components/awpIcons';
import SensorEquipmentService from '@components/SensorAdmin/SensorEquipmentService';

const SensorTable = ({
  isMedium,
  isLarge,
  data,
  loading,
  setMode,
  mode,
  setSensor,
  search,
  setSearch,
  filteredData,
  setFilteredData,
  defaultPageSize,
  setDefaultPageSize,
}) => {
  const crudService = new SensorEquipmentService(mode);

  const actionMenu = (row) => {
    return (
      <>
        <div>
          <Authorize requires={[Permissions.WriteSensorEquipment]}>
            <ActionMenuEdit
              onClick={() => {
                setMode(FORM_MODE.EDIT);
                setSensor(row.row.original);
              }}
            />
          </Authorize>
          <ActionMenuView
            onClick={() => {
              setMode(FORM_MODE.VIEW);
              setSensor(row.row.original);
            }}
          />
        </div>
      </>
    );
  };
  const hasSearch = (src) => {
    return src ? src.toLowerCase().includes(search.toLowerCase()) : false;
  };
  useEffect(() => {
    let filtered = [...data];
    if (!isValueEmpty(search)) {
      filtered = filtered.filter(
        (sensor) =>
          hasSearch(sensor.Manufacturer) || //here is the name of manufacturer
          hasSearch(sensor.ModelNumber) ||
          hasSearch(sensor.ModelId + '') ||
          hasSearch(sensor.FirmwareVersion) ||
          hasSearch(sensor.SerialNumber) ||
          hasSearch(sensor.SensorTypeName) ||
          hasSearch(sensor.Id + '') ||
          (sensor.WeatherStation && !isValueEmpty(sensor.WeatherStation.Name)
            ? hasSearch(sensor.WeatherStation.Name)
            : false) ||
          (sensor.WeatherStation && !isValueEmpty(sensor.WeatherStation.Number)
            ? hasSearch(sensor.WeatherStation.Number)
            : false),
      );
    }

    setFilteredData(filtered);
  }, [search]);
  const cellComponentExp = ({ row }) => (
    // Use Cell to render an expander for each row.
    // We can use the getToggleRowExpandedProps prop-getter
    // to build the expander.
    <span {...row.getToggleRowExpandedProps()}>{row.isExpanded ? <IconCaretDownFill /> : <IconCaretRightFill />}</span>
  );

  const cellComponentWs = (p) => {
    return (
      <>
        {p.value && p.value.Id ? (
          <Link to={Views.WeatherStation} state={{ id: p.value.Id, mode: FORM_MODE.VIEW }}>
            {p.value.Number + ' - ' + p.value.Name}
          </Link>
        ) : (
          ''
        )}
      </>
    );
  };
  const columns = React.useMemo(
    () => [
      {
        // Make an expander cell
        Header: () => null, // No header
        id: 'expander', // It needs an ID
        Cell: cellComponentExp,
      },
      {
        Header: 'ID',
        accessor: 'Id',
      },
      {
        Header: 'Model',
        accessor: 'ModelNumber',
        sortType: sortByNumericThenAlpha,
      },
      {
        Header: 'Manufacturer',
        accessor: 'Manufacturer',
        show: isMedium,
        sortType: sortByNumericThenAlpha,
      },
      {
        Header: 'Serial number',
        accessor: 'SerialNumber',
        show: isMedium,
        sortType: sortByNumericThenAlpha,
      },
      {
        Header: 'Firmware version',
        accessor: 'FirmwareVersion',
        show: isLarge,
        sortType: sortByNumericThenAlpha,
      },
      {
        Header: 'Weather station',
        accessor: 'WeatherStation',
        show: isLarge,
        sortType: (rowA, rowB, id) => {
          let valA = '';
          let valB = '';

          if (rowA.original[id] && rowA.original[id].Number) {
            valA = rowA.original[id].Number;
          }

          if (rowB.original[id] && rowB.original[id].Number) {
            valB = rowB.original[id].Number;
          }

          return valA.localeCompare(valB);
        },
        Cell: cellComponentWs,
      },
      {
        Header: 'Installation date',
        accessor: 'InstallationDate',
        sortType: sortByNumericThenAlpha,
      },
      {
        Header: 'Decommission date',
        accessor: 'DecommissionDate',
        sortType: sortByNumericThenAlpha,
      },
      {
        Header: 'Status',
        accessor: 'SensorStatusName',
        sortType: sortByNumericThenAlpha,
      },
      {
        Header: 'Actions',
        accessor: 'Id',
        id: 'SEActionsId',
        disableSortBy: true,
        Cell: actionMenu,
      },
    ],
    [isLarge, isMedium],
  );
  const renderRowSubComponent = useCallback(
    ({ row }) => (
      <table>
        <tbody>
          <tr>
            <td className="awp_table_expend_row_details" style={{ fontWeight: 'bold' }}>
              Installation date
            </td>
            <td className="awp_table_expend_row_details">{row.values['InstallationDate']}</td>
          </tr>
          <tr>
            <td className="awp_table_expend_row_details" style={{ fontWeight: 'bold' }}>
              Decommission date
            </td>
            <td className="awp_table_expend_row_details">{row.values['DecommissionDate']}</td>
          </tr>
          <tr>
            <td className="awp_table_expend_row_details" style={{ fontWeight: 'bold' }}>
              Status
            </td>
            <td className="awp_table_expend_row_details">{row.values['SensorStatusName']}</td>
          </tr>
        </tbody>
      </table>
    ),
    [],
  );
  return (
    <>
      <div className="d-flex">{crudService.crud.getHeader()}</div>
      <div className="container mb-3">
        <div className="row">
          <div className="col-7 pl-0 pr-0">
            <div className="col-md-6 col-sm-12 pl-0">
              <input
                className="form-control float-left"
                placeholder="Search..."
                id="user_text_search"
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              ></input>
            </div>
          </div>
        </div>
      </div>
      <Table
        columns={columns}
        data={filteredData}
        fetchData={() => {}}
        loading={loading}
        search=""
        renderRowSubComponent={renderRowSubComponent}
        defaultSortBy={'Id'}
        hiddenColumns={['InstallationDate', 'DecommissionDate', 'SensorStatusName']}
        defaultPageSize={defaultPageSize}
        setDefaultPageSize={setDefaultPageSize}
      />
      <div className="container mb-3" style={{ paddingTop: '1.25rem' }}>
        <div className="row">
          <div className="col-md-12 col-lg-12 col-sm-12 pr-0 pl-0">
            <Authorize requires={[Permissions.WriteSensorEquipment]}>
              <Button
                className="mr-2 mr-mini-0 float-left"
                variant="primary"
                onClick={() => {
                  setMode(FORM_MODE.CREATE);
                }}
                disabled={loading}
              >
                Create
              </Button>
            </Authorize>
          </div>
        </div>
      </div>
    </>
  );
};

export default SensorTable;
